import { ExternalLinkIcon, MessageSquareTextIcon, MoreHorizontalIcon, PencilIcon, PlusIcon, PrinterIcon, QrCodeIcon, SendIcon, Trash2Icon, UserPlusIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { TableSkeleton } from '@/components/LoadingBlock';
import { SectionTitle } from '@/components/PageHeader';
import { PageIntro } from '@/components/PageIntro';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useTenantSettings } from '@/features/org/queries';
import { useJobContext } from '@/layouts/JobLayout';
import { errorMessage } from '@/lib/api';
import { useMe } from '@/lib/auth';
import { fmtDateTime, fmtRelative, truncate } from '@/lib/format';
import type { KeywordDto, KeywordInput } from '@/lib/types';
import { cn } from '@/lib/utils';
import { Bubble, KeywordDialog } from './KeywordDialog';
import { useKeywordActivity, useKeywordMutations, useKeywordQr, useKeywords } from './queries';

export function KeywordsPage() {
  const { job, permissions } = useJobContext();
  const me = useMe();
  const data = useKeywords(job.id);
  const settings = useTenantSettings();
  const m = useKeywordMutations(job.id);
  const [dialog, setDialog] = useState<{ open: boolean; keyword: KeywordDto | null; kind: 'info' | 'join' }>({ open: false, keyword: null, kind: 'info' });
  const { keywordId } = useParams<{ keywordId?: string }>();
  const navigate = useNavigate();
  // Deep link: /jobs/:jobId/keywords/:keywordId opens that keyword once the list is in.
  const linkedKeyword = keywordId ? (data.data?.items ?? []).find((k) => k.id === keywordId) ?? null : null;
  useEffect(() => {
    if (linkedKeyword) setDialog({ open: true, keyword: linkedKeyword, kind: linkedKeyword.kind === 'join' ? 'join' : 'info' });
  }, [linkedKeyword]);
  const [deleting, setDeleting] = useState<KeywordDto | null>(null);

  if (data.isPending) return <TableSkeleton rows={4} cols={5} />;
  if (data.isError) return <ErrorState error={data.error} onRetry={() => void data.refetch()} />;

  const { items, number, features, policy, languages } = data.data;
  const catalog = settings.data?.languages ?? [];
  const defaultLanguage = settings.data?.settings.i18n.defaultLanguage ?? 'en';
  const join = items.find((k) => k.kind === 'join') ?? null;
  const infos = items.filter((k) => k.kind === 'info');
  const canManage = permissions.manageKeywords;

  if (!features.keywords) {
    return (
      <EmptyState
        icon={MessageSquareTextIcon}
        title="Keyword replies are turned off"
        description="Your organization has disabled keyword auto-replies. An organization admin can turn them on under Organization → Settings."
        action={me.permissions.manageOrgSettings ? <Button asChild variant="outline"><Link to="/org/settings">Open organization settings</Link></Button> : undefined}
      />
    );
  }

  const submit = async (body: KeywordInput, id: string | null) => {
    if (id) await m.update.mutateAsync({ id, body });
    else await m.create.mutateAsync(body);
  };

  const toggle = async (k: KeywordDto) => {
    try {
      if (k.inherited) {
        // Overriding a tenant default: create a disabled job-level copy so this job stops answering it.
        await m.create.mutateAsync({ keyword: k.keyword, kind: k.kind, replyBody: k.replyBody, replyTranslations: k.replyTranslations, settings: k.settings, enabled: !k.enabled });
      } else await m.update.mutateAsync({ id: k.id, body: { enabled: !k.enabled } });
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const menu = (k: KeywordDto) =>
    canManage ? (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon-sm" aria-label={`Actions for ${k.keyword}`}>
            <MoreHorizontalIcon />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {k.inherited ? (
            <DropdownMenuItem onClick={() => setDialog({ open: true, keyword: { ...k, id: '' }, kind: 'info' })}>
              <PencilIcon /> Override for this job
            </DropdownMenuItem>
          ) : (
            <>
              <DropdownMenuItem onClick={() => setDialog({ open: true, keyword: k, kind: 'info' })}>
                <PencilIcon /> Edit
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem variant="destructive" onClick={() => setDeleting(k)}>
                <Trash2Icon /> Delete
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    ) : null;

  return (
    <div className="space-y-6 sm:space-y-8">
      <PageIntro
        title="Keywords"
        description={
          number ? (
            <>
              Text a keyword to <span className="text-foreground font-medium tabular-nums">{number.display}</span> and get an instant reply. The job number doubles as the site&apos;s info line.
            </>
          ) : (
            'Assign a phone number to this job to start answering keywords.'
          )
        }
        actions={
          canManage ? (
            <Button onClick={() => setDialog({ open: true, keyword: null, kind: 'info' })}>
              <PlusIcon /> Add keyword
            </Button>
          ) : undefined
        }
      />

      {!policy.allowJobKeywords && !canManage && permissions.editJob && (
        <p className="text-muted-foreground border border-dashed p-3 text-sm">Your organization manages keywords centrally. Organization admins add or change them under Organization → Settings.</p>
      )}

      {features.selfServiceJoin && <JoinCard join={join} jobId={job.id} number={number} canManage={canManage} onEdit={() => setDialog({ open: true, keyword: join, kind: 'join' })} onCreate={() => setDialog({ open: true, keyword: null, kind: 'join' })} onToggle={join ? () => void toggle(join) : undefined} />}

      <section className="space-y-3">
        <SectionTitle>Info keywords</SectionTitle>
        {infos.length === 0 ? (
          <EmptyState
            icon={MessageSquareTextIcon}
            title="No keywords yet"
            description="Start with the questions the trailer answers ten times a day: GATE, PARKING, HOURS, SAFETY."
            action={canManage ? <Button onClick={() => setDialog({ open: true, keyword: null, kind: 'info' })}><PlusIcon /> Add keyword</Button> : undefined}
            className="py-10"
          />
        ) : (
          <>
          <ul className="bg-card divide-y border md:hidden" aria-label="Info keywords">
            {infos.map((k) => (
              <li key={k.id} className={cn('grid gap-2 px-4 py-3', !k.enabled && 'opacity-60')}>
                <div className="flex items-center justify-between gap-3">
                  <span className="flex min-w-0 items-center gap-2">
                    <span className="font-mono font-semibold">{k.keyword}</span>
                    {k.inherited && <Badge variant="muted">Org default</Badge>}
                  </span>
                  <span className="flex shrink-0 items-center gap-1">
                    <Switch checked={k.enabled} disabled={!canManage || m.update.isPending || m.create.isPending} onCheckedChange={() => void toggle(k)} aria-label={`Toggle ${k.keyword}`} />
                    {menu(k)}
                  </span>
                </div>
                <p className="text-muted-foreground text-sm leading-snug">{truncate(k.replyBody, 160)}</p>
                <p className="text-muted-foreground text-xs tabular-nums">
                  {k.hitCount} {k.hitCount === 1 ? 'hit' : 'hits'} · {k.lastHitAt ? `last ${fmtRelative(k.lastHitAt)}` : 'never used'}
                </p>
              </li>
            ))}
          </ul>
          <div className="bg-card hidden overflow-x-auto border md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Keyword</TableHead>
                  <TableHead>Reply</TableHead>
                  <TableHead>Languages</TableHead>
                  <TableHead className="text-right">Hits</TableHead>
                  <TableHead>Last used</TableHead>
                  <TableHead>On</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {infos.map((k) => (
                  <TableRow key={k.id} className={!k.enabled ? 'opacity-60' : undefined}>
                    <TableCell>
                      <span className="font-mono font-semibold">{k.keyword}</span>
                      {k.inherited && (
                        <Badge variant="muted" className="ml-2">
                          Org default
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="max-w-md whitespace-normal text-muted-foreground">{truncate(k.replyBody, 120)}</TableCell>
                    <TableCell className="text-muted-foreground text-xs">
                      {[defaultLanguage, ...Object.keys(k.replyTranslations)].map((c) => catalog.find((l) => l.code === c)?.name ?? c).join(', ')}
                      {languages.filter((l) => l !== defaultLanguage && !k.replyTranslations[l]).length > 0 && <span className="text-muted-foreground/70"> · others auto-translated</span>}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{k.hitCount}</TableCell>
                    <TableCell className="text-muted-foreground">{k.lastHitAt ? fmtRelative(k.lastHitAt) : '—'}</TableCell>
                    <TableCell>
                      <Switch checked={k.enabled} disabled={!canManage || m.update.isPending || m.create.isPending} onCheckedChange={() => void toggle(k)} aria-label={`Toggle ${k.keyword}`} />
                    </TableCell>
                    <TableCell>{menu(k)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          </>
        )}
      </section>

      <div className="grid grid-cols-[minmax(0,1fr)] gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
        <TryIt jobId={job.id} number={number?.display ?? job.name} languages={languages} catalog={catalog} />
        <Activity jobId={job.id} />
      </div>

      <KeywordDialog
        open={dialog.open}
        onOpenChange={(o) => {
          setDialog((d) => ({ ...d, open: o }));
          if (!o && keywordId) navigate(`/jobs/${job.id}/keywords`, { replace: true });
        }}
        keyword={dialog.keyword?.id ? dialog.keyword : null}
        defaultKind={dialog.kind}
        languages={languages}
        defaultLanguage={defaultLanguage}
        languageCatalog={catalog}
        joinDefaults={policy.joinDefaults}
        allowJoin={features.selfServiceJoin && !join}
        jobName={job.name}
        scopeLabel="Applies to this job only."
        onSubmit={async (body, id) => {
          // "Override" of an inherited keyword arrives with an empty id and creates a job-level copy.
          if (!id && dialog.keyword && !dialog.keyword.id) return m.create.mutateAsync(body);
          return submit(body, id);
        }}
        busy={m.create.isPending || m.update.isPending}
      />
      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(o) => !o && setDeleting(null)}
        title={`Delete ${deleting?.keyword}?`}
        description="People who text it will no longer get a reply. Past activity is kept."
        confirmLabel="Delete"
        destructive
        loading={m.remove.isPending}
        onConfirm={async () => {
          if (!deleting) return;
          try {
            await m.remove.mutateAsync(deleting.id);
            toast.success('Keyword deleted');
            setDeleting(null);
          } catch (err) {
            toast.error(errorMessage(err));
          }
        }}
      />
    </div>
  );
}

function JoinCard({ join, jobId, number, canManage, onEdit, onCreate, onToggle }: { join: KeywordDto | null; jobId: string; number: { e164: string; display: string } | null; canManage: boolean; onEdit: () => void; onCreate: () => void; onToggle?: () => void }) {
  const qr = useKeywordQr(jobId, join && number ? join.id : null, 256);
  if (!join) {
    return (
      <section className="grid gap-4 border border-dashed p-5 sm:grid-cols-[1fr_auto] sm:items-center">
        <div>
          <h2 className="flex items-center gap-2 text-sm font-semibold">
            <UserPlusIcon className="size-4" /> Self-service sign-up
          </h2>
          <p className="text-muted-foreground mt-1 text-sm">
            Print a sign for the gate. Workers text <span className="font-mono font-medium">JOIN</span> (or scan the QR code), pick their language and trade, and land on this job&apos;s list with a TCPA consent record. No spreadsheets.
          </p>
        </div>
        {canManage && (
          <Button onClick={onCreate}>
            <QrCodeIcon /> Set up sign-up
          </Button>
        )}
      </section>
    );
  }
  const steps = [join.settings.askLanguage ?? true ? 'language' : null, join.settings.askName ?? true ? 'name' : null, join.settings.askTrade ?? true ? 'trade' : null, join.settings.askCompany ?? true ? 'company' : null].filter(Boolean).length;
  return (
    <section className="grid gap-5 border bg-card p-5 lg:grid-cols-[auto_1fr_auto]">
      <div className="flex items-center justify-center border bg-white p-2" aria-label="QR code" style={{ width: 132, height: 132 }}>
        {qr.data ? <div className="[&>svg]:size-full" dangerouslySetInnerHTML={{ __html: qr.data }} /> : <QrCodeIcon className="text-muted-foreground size-10" />}
      </div>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-sm font-semibold">Self-service sign-up</h2>
          <Badge variant={join.enabled ? 'success' : 'muted'}>{join.enabled ? 'On' : 'Off'}</Badge>
          {join.inherited && <Badge variant="muted">Org default</Badge>}
        </div>
        <p className="mt-1 text-lg">
          Text <span className="font-mono font-semibold">{join.keyword}</span> to <span className="font-medium tabular-nums">{number?.display ?? 'the job number'}</span>
        </p>
        <p className="text-muted-foreground mt-1 text-sm">
          {steps} question{steps === 1 ? '' : 's'} · {join.hitCount} sign-up{join.hitCount === 1 ? '' : 's'} so far{join.lastHitAt ? ` · last ${fmtRelative(join.lastHitAt)}` : ''}
        </p>
        <p className="text-muted-foreground mt-2 line-clamp-2 text-xs">{join.settings.consentText ?? 'Default consent disclosure.'}</p>
      </div>
      <div className="flex flex-col gap-2 sm:flex-row lg:flex-col">
        {number && (
          <Button asChild variant="outline">
            <Link to={`/jobs/${jobId}/keywords/${join.id}/sign`} target="_blank" rel="noreferrer">
              <PrinterIcon /> Print sign
            </Link>
          </Button>
        )}
        {canManage && (
          <>
            <Button variant="outline" onClick={onEdit}>
              <PencilIcon /> {join.inherited ? 'Customize' : 'Edit'}
            </Button>
            {onToggle && (
              <Button variant="ghost" onClick={onToggle}>
                {join.enabled ? 'Turn off' : 'Turn on'}
              </Button>
            )}
          </>
        )}
      </div>
    </section>
  );
}

function TryIt({ jobId, number, languages, catalog }: { jobId: string; number: string; languages: string[]; catalog: Array<{ code: string; name: string }> }) {
  const m = useKeywordMutations(jobId);
  const [text, setText] = useState('GATE');
  const [language, setLanguage] = useState(languages[0] ?? 'en');
  const result = m.test.data;
  return (
    <section className="bg-card min-w-0 border p-5">
      <SectionTitle className="mb-3">Try it</SectionTitle>
      <p className="text-muted-foreground mb-3 text-xs">See which keyword answers a text and exactly what the person gets, without sending anything.</p>
      <div className="flex flex-wrap gap-2">
        <Input className="min-w-[9rem] flex-1" value={text} onChange={(e) => setText(e.target.value)} placeholder="What a worker might text" onKeyDown={(e) => e.key === 'Enter' && text.trim() && m.test.mutate({ body: text, language })} />
        {languages.length > 1 && (
          <select value={language} onChange={(e) => setLanguage(e.target.value)} className="border-input bg-background h-9 border px-2 text-sm" aria-label="Recipient language">
            {languages.map((l) => (
              <option key={l} value={l}>
                {catalog.find((c) => c.code === l)?.name ?? l}
              </option>
            ))}
          </select>
        )}
        <Button onClick={() => m.test.mutate({ body: text, language })} disabled={!text.trim() || m.test.isPending}>
          <SendIcon /> Test
        </Button>
      </div>
      <div className="bg-muted/40 mt-4 border p-3">
        <div className="text-muted-foreground border-b pb-1 text-center text-[11px]">{number}</div>
        <div className="grid gap-2 py-3 text-xs">
          <Bubble who="worker">{m.test.variables?.body ?? text}</Bubble>
          {m.test.isPending ? (
            <p className="text-muted-foreground text-[11px]">…</p>
          ) : result ? (
            result.matched ? (
              <Bubble who="us">{result.reply}</Bubble>
            ) : (
              <p className="text-muted-foreground text-[11px]">No keyword matches — the text is stored but nobody is replied to.</p>
            )
          ) : null}
        </div>
      </div>
    </section>
  );
}

function Activity({ jobId }: { jobId: string }) {
  const activity = useKeywordActivity(jobId, { page: 1, pageSize: 10 });
  const items = activity.data?.items ?? [];
  const counts = activity.data?.counts ?? [];
  return (
    <section className="bg-card min-w-0 border p-5">
      <SectionTitle className="mb-3">Recent activity</SectionTitle>
      {counts.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-1.5">
          {counts.map((c) => (
            <Badge key={c.keywordId ?? 'help'} variant="secondary">
              {c.keyword ?? 'HELP'} · {c.count} in 30 days
            </Badge>
          ))}
        </div>
      )}
      {items.length === 0 ? (
        <p className="text-muted-foreground text-sm">No automatic replies yet. They will show up here as workers text the number.</p>
      ) : (
        <ul className="divide-y text-sm">
          {items.map((a) => (
            <li key={a.id} className="grid grid-cols-[minmax(0,1fr)] gap-0.5 py-2">
              <div className="flex items-center justify-between gap-2">
                <span className="truncate tabular-nums">{a.toDisplay}</span>
                <span className="text-muted-foreground text-xs">{fmtDateTime(a.createdAt)}</span>
              </div>
              <div className="text-muted-foreground flex items-center gap-2 text-xs">
                {a.inboundBody && <span className="shrink-0 font-mono">“{truncate(a.inboundBody, 24)}”</span>}
                <span>→</span>
                <span className="min-w-0 flex-1 truncate">{truncate(a.body, 90)}</span>
                <Badge variant={a.status === 'failed' ? 'danger' : a.status === 'delivered' ? 'success' : 'muted'} className="ml-auto">
                  {a.kind === 'onboarding' ? 'sign-up' : a.kind}
                </Badge>
              </div>
            </li>
          ))}
        </ul>
      )}
      {activity.data && activity.data.total > 10 && (
        <p className="text-muted-foreground mt-2 flex items-center gap-1 text-xs">
          <ExternalLinkIcon className="size-3" /> {activity.data.total.toLocaleString()} replies total.
        </p>
      )}
    </section>
  );
}
